/**
 * Import Progress Indicator — the "recently completed" section.
 *
 * EVERY successful import lands here, which is the whole point: the panel used
 * to render a completion only when the job carried an unknown-rows auto-ticket,
 * so an ordinary clean import ended with no confirmation at all. Entries that
 * DO carry a ticket keep the richer banner variant (`CompletedTicketItem`),
 * whose copy + testIds mirror the editor banner verbatim.
 *
 * Extracted from ImportProgressIndicator.tsx to keep that file under the
 * 500-line LLM ceiling (CLAUDE.md §0).
 */

import { useTranslation } from 'react-i18next'

import type { UnknownRowsTicketBanner } from '@/modules/accounting/features/import/components/custom-importer/sections/ResultsPreviewSection'

import { CompletedTicketItem } from './CompletedTicketItem'
import { ImportCompletedItem } from './ImportCompletedItem'

/** One recently finished import, with or without an unknown-rows ticket. */
export interface CompletedImportEntry {
  jobId: string
  sourceName: string
  completedAt: Date
  /** Rows booked, when the API reported a count — never fabricated. */
  importedRecordCount: number | null
  ticket: UnknownRowsTicketBanner | null
}

interface RecentlyCompletedSectionProps {
  entries: CompletedImportEntry[]
  onDismiss: (jobId: string) => void
  formatRelativeTime: (date: Date) => string
}

export const RecentlyCompletedSection = ({
  entries,
  onDismiss,
  formatRelativeTime,
}: RecentlyCompletedSectionProps) => {
  const { t } = useTranslation('imports')

  if (entries.length === 0) return null

  return (
    <div className="pt-1 border-t border-crypto-border space-y-1.5">
      <div className="text-xs font-medium text-crypto-muted pt-2">
        {t('progressIndicator.recentlyCompleted', 'Recently completed')}
      </div>
      {entries.map((entry) =>
        entry.ticket ? (
          <CompletedTicketItem
            key={entry.jobId}
            entry={{
              jobId: entry.jobId,
              sourceName: entry.sourceName,
              completedAt: entry.completedAt,
              ticket: entry.ticket,
            }}
            onDismiss={() => onDismiss(entry.jobId)}
            formatRelativeTime={formatRelativeTime}
          />
        ) : (
          <ImportCompletedItem
            key={entry.jobId}
            entry={{
              jobId: entry.jobId,
              sourceName: entry.sourceName,
              completedAt: entry.completedAt,
              importedRecordCount: entry.importedRecordCount,
            }}
            onDismiss={() => onDismiss(entry.jobId)}
            formatRelativeTime={formatRelativeTime}
          />
        ),
      )}
    </div>
  )
}
